import { DialogComponent } from '@theme/dialog';

const storageKey = 'kk-customer-popup-v1';
const pendingKey = `${storageKey}-submitted`;
const seenKey = `${storageKey}-seen`;
const day = 86400000;

/** Newsletter popup using the theme's modal, focus, Escape and scroll-lock behavior. */
class KankaCustomerPopup extends DialogComponent {
  #timer;
  #listeners;
  #attempts = 0;
  #memoryUntil = 0;
  #preview = false;

  connectedCallback() {
    super.connectedCallback();
    // The server's non-modal form remains usable when JavaScript is unavailable.
    if (!this.hasAttribute('data-enhanced')) this.refs.dialog.close();
    this.setAttribute('data-enhanced', '');
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#listeners?.abort();
    this.refs.dialog?.close();
    super.disconnectedCallback();
  }

  #get(storage, key) {
    try { return window[storage].getItem(key); } catch { return null; }
  }

  #set(storage, key, value) {
    try { window[storage].setItem(key, value); } catch { /* In-memory dismissal still works. */ }
  }

  #initialize() {
    clearTimeout(this.#timer);
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    const params = new URL(location.href).searchParams;
    this.#preview = params.get('kk_popup') === 'preview'
      && window.Shopify?.theme?.id === 210249711884
      && window.Shopify?.theme?.role === 'unpublished';

    this.addEventListener('dialog:open', () => {
      clearTimeout(this.#timer);
      if (!window.Shopify?.designMode && !this.#preview) this.#set('sessionStorage', seenKey, '1');
    }, options);
    this.addEventListener('dialog:close', () => {
      if (window.Shopify?.designMode || this.#preview) return;
      this.#memoryUntil = Math.max(this.#memoryUntil, Date.now() + Number(this.dataset.repeatDays) * day);
      this.#set('localStorage', storageKey, String(Math.max(Number(this.#get('localStorage', storageKey)) || 0, this.#memoryUntil)));
    }, options);
    this.querySelector('form')?.addEventListener('submit', () => {
      this.#set('sessionStorage', pendingKey, '1');
    }, options);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', event => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.showDialog();
      }, options);
      document.addEventListener('shopify:section:deselect', event => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.closeDialog();
      }, options);
      return;
    }

    const feedback = this.querySelector('[data-feedback]')?.dataset.feedback;
    const submitted = this.#get('sessionStorage', pendingKey) === '1';
    if (submitted && feedback) {
      this.#set('sessionStorage', pendingKey, '');
      if (feedback === 'success') {
        this.#memoryUntil = Date.now() + 180 * day;
        this.#set('localStorage', storageKey, String(this.#memoryUntil));
      }
      this.showDialog();
      return;
    }
    if (this.#preview) { this.showDialog(); return; }
    if (Math.max(Number(this.#get('localStorage', storageKey)) || 0, this.#memoryUntil) > Date.now()
      || this.#get('sessionStorage', seenKey) === '1') return;
    this.#attempts = 0;
    this.#timer = setTimeout(() => this.#showWhenAvailable(), Number(this.dataset.delay) * 1000);
  }

  #showWhenAvailable() {
    if (!this.isConnected) return;
    const focused = document.activeElement;
    if (document.hidden || document.querySelector('dialog[open]')
      || focused?.matches('input, textarea, select, [contenteditable="true"]')) {
      if (++this.#attempts < 30) this.#timer = setTimeout(() => this.#showWhenAvailable(), 1000);
      return;
    }
    this.showDialog();
  }
}

if (!customElements.get('kk-customer-popup')) customElements.define('kk-customer-popup', KankaCustomerPopup);
